import type {
  BillableKmDict,
  BillingPeriodResponse,
  DetailedInvoiceRow,
  DistrictRequirementsModel,
  DriveSubinterval,
  DriveType,
  RequirementFulfillment,
  RequirementSubinterval,
} from '@/api/main/types.gen'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { uniq } from 'lodash-es'
import { formatIntervalClock, intervalsMatch, type IntervalPresets, type TimeInterval } from './autoScheduleIntervals'
import { getTimezone } from './datetime'
import { findBillingPeriodForInterval } from './scheduling'

dayjs.extend(utc)
dayjs.extend(timezone)

export type DriveTypeCombination = DriveType[]

export const driveTypeKey = (driveType: DriveType[]): string =>
  [...driveType].sort((a, b) => (a < b ? 1 : -1)).join('+')

export const driveTypeLabel = (driveType: DriveType[]): string =>
  [...driveType]
    .sort((a, b) => (a < b ? 1 : -1))
    .map(type => type[0]?.toUpperCase() ?? '')
    .join('')

export type RequirementIntervalChip = {
  id: string
  interval: TimeInterval
  count: number
  rangeLabel: string
}

const toUtcInterval = (start: string, end: string): TimeInterval => ({
  start: dayjs.utc(start).format('YYYY-MM-DDTHH:mm:ss[Z]'),
  end: dayjs.utc(end).format('YYYY-MM-DDTHH:mm:ss[Z]'),
})

const intervalContainedInWindow = (interval: TimeInterval, windowStart: string, windowEnd: string): boolean => {
  const start = dayjs.utc(interval.start)
  const end = dayjs.utc(interval.end)
  return !start.isBefore(dayjs.utc(windowStart)) && !end.isAfter(dayjs.utc(windowEnd)) && start.isBefore(end)
}

export const requirementWindowCoversInterval = (
  windowStart: string,
  windowEnd: string,
  intervalStart: string,
  intervalEnd: string
): boolean => intervalContainedInWindow({ start: intervalStart, end: intervalEnd }, windowStart, windowEnd)

const matchesShiftPreset = (interval: TimeInterval, presets: IntervalPresets): boolean =>
  Boolean(
    (presets.day && intervalsMatch(interval, presets.day)) ||
      (presets.night && intervalsMatch(interval, presets.night)) ||
      intervalsMatch(interval, presets.full)
  )

const chipGroupKey = (interval: TimeInterval): string => {
  const tz = getTimezone()
  const start = dayjs.utc(interval.start).tz(tz).format('YYYY-MM-DDTHH:mm')
  const end = dayjs.utc(interval.end).tz(tz).format('YYYY-MM-DDTHH:mm')
  return `${start}/${end}`
}

export const uniqueDriveTypeCombinations = (requirements: DistrictRequirementsModel[]): DriveTypeCombination[] => {
  const seen = new Set<string>()
  const combinations: DriveTypeCombination[] = []
  for (const requirement of requirements) {
    const sorted = [...requirement.drive_type].sort((a, b) => (a < b ? 1 : -1))
    const key = sorted.join('+')
    if (!seen.has(key)) {
      seen.add(key)
      combinations.push(sorted)
    }
  }
  return combinations
}

const flattenedDriveTypes = (selected: DriveTypeCombination[]): DriveType[] => uniq(selected.flat())

export const requirementMatchesDriveTypeFilter = (
  requirement: DistrictRequirementsModel,
  selectedCombinations: DriveTypeCombination[]
): boolean => {
  const allowed = flattenedDriveTypes(selectedCombinations)
  return requirement.drive_type.every(type => allowed.includes(type))
}

export const buildRequirementIntervalChips = ({
  requirementSubintervals,
  windowStart,
  windowEnd,
  overlappingBillingPeriods,
  presets,
  requirements,
  selectedCombinations,
}: {
  requirementSubintervals: RequirementSubinterval[]
  windowStart: string
  windowEnd: string
  overlappingBillingPeriods: BillingPeriodResponse[]
  presets: IntervalPresets
  requirements: DistrictRequirementsModel[]
  selectedCombinations: DriveTypeCombination[]
}): RequirementIntervalChip[] => {
  const grouped = new Map<string, { interval: TimeInterval; requirementIds: Set<number> }>()
  const requirementsById = new Map(requirements.map(requirement => [requirement.id, requirement]))

  for (const subinterval of requirementSubintervals) {
    const interval = toUtcInterval(subinterval.interval.starting_from, subinterval.interval.ending_at)
    if (!intervalContainedInWindow(interval, windowStart, windowEnd)) {
      continue
    }
    if (findBillingPeriodForInterval(interval.start, interval.end, overlappingBillingPeriods) === null) {
      continue
    }
    if (matchesShiftPreset(interval, presets)) {
      continue
    }
    const matchingIds = subinterval.requirements.filter(id => {
      const requirement = requirementsById.get(id)
      return requirement !== undefined && requirementMatchesDriveTypeFilter(requirement, selectedCombinations)
    })
    if (matchingIds.length === 0) {
      continue
    }
    const key = chipGroupKey(interval)
    const existing = grouped.get(key)
    if (existing) {
      for (const id of matchingIds) {
        existing.requirementIds.add(id)
      }
    } else {
      grouped.set(key, { interval, requirementIds: new Set(matchingIds) })
    }
  }

  const chips = [...grouped.values()]
    .sort((a, b) => {
      const startDiff = dayjs.utc(a.interval.start).valueOf() - dayjs.utc(b.interval.start).valueOf()
      if (startDiff !== 0) {
        return startDiff
      }
      return dayjs.utc(a.interval.end).valueOf() - dayjs.utc(b.interval.end).valueOf()
    })
    .map(({ interval, requirementIds }) => ({
      interval,
      count: requirementIds.size,
      clockLabel: formatIntervalClock(interval),
    }))

  const clockCounts = chips.reduce((counts, chip) => {
    counts.set(chip.clockLabel, (counts.get(chip.clockLabel) ?? 0) + 1)
    return counts
  }, new Map<string, number>())

  const tz = getTimezone()
  return chips.map(chip => {
    const rangeLabel =
      (clockCounts.get(chip.clockLabel) ?? 0) > 1
        ? `${dayjs.utc(chip.interval.start).tz(tz).format('D. M.')} ${chip.clockLabel}`
        : chip.clockLabel
    return {
      id: `req:${chip.interval.start}:${chip.interval.end}`,
      interval: chip.interval,
      count: chip.count,
      rangeLabel,
    }
  })
}

export const partitionRequirementsForInterval = ({
  requirements,
  requirementSubintervals,
  selectedCombinations,
  intervalStart,
  intervalEnd,
}: {
  requirements: DistrictRequirementsModel[]
  requirementSubintervals: RequirementSubinterval[]
  selectedCombinations: DriveTypeCombination[]
  intervalStart: string
  intervalEnd: string
}): { relevant: DistrictRequirementsModel[]; irrelevant: DistrictRequirementsModel[] } => {
  const matchedIds = new Set(
    requirementSubintervals
      .filter(({ interval }) =>
        requirementWindowCoversInterval(interval.starting_from, interval.ending_at, intervalStart, intervalEnd)
      )
      .flatMap(({ requirements: ids }) => ids)
  )

  const filtered = [...requirements]
    .filter(requirement => requirementMatchesDriveTypeFilter(requirement, selectedCombinations))
    .sort((a, b) =>
      `${a.district_code}-${a.subdistrict_code}`.localeCompare(`${b.district_code}-${b.subdistrict_code}`)
    )

  return {
    relevant: filtered.filter(requirement => matchedIds.has(requirement.id)),
    irrelevant: filtered.filter(requirement => !matchedIds.has(requirement.id)),
  }
}

export type RequirementKmTotals = {
  drivenKm: number
  plannedKm: number
  orderedKm: number
  fulfillableKm: number
  remainingKm: number
}

export const sumBillableKm = (driveType: DriveType[], billableKm: BillableKmDict | undefined): RequirementKmTotals => {
  const drivenKm = driveType.reduce((sum, type) => sum + (billableKm?.[type]?.driven_km ?? 0), 0)
  const plannedKm = driveType.reduce((sum, type) => sum + (billableKm?.[type]?.planned_km ?? 0), 0)
  const orderedKm = driveType.reduce((sum, type) => sum + (billableKm?.[type]?.ordered_km ?? 0), 0)
  const fulfillableKm = driveType.reduce((sum, type) => sum + (billableKm?.[type]?.fulfillable_km ?? 0), 0)
  return {
    drivenKm,
    plannedKm,
    orderedKm,
    fulfillableKm,
    remainingKm: orderedKm - drivenKm - plannedKm,
  }
}

export const requirementSlack = (
  totals: RequirementKmTotals,
  remainingTime: number | undefined,
  totalTime: number | undefined
): number | undefined => {
  if (remainingTime === undefined || totalTime === undefined || totals.orderedKm === 0 || totalTime === 0) {
    return undefined
  }
  return totals.remainingKm / totals.orderedKm - remainingTime / totalTime
}

export const formatSlackPercent = (slack: number | undefined): string =>
  slack === undefined ? '—' : `${(100 * slack).toFixed(0)}%`

export const exploitableOverheadKm = (requirementId: number, invoiceRows: DetailedInvoiceRow[]): number | undefined => {
  const row = invoiceRows.find(invoiceRow => invoiceRow.master_id === requirementId)
  if (!row) {
    return undefined
  }
  return row.km_stat_ordered - row.km_stat
}

export const totalExploitableOverheadKm = (
  requirements: DistrictRequirementsModel[],
  invoiceRows: DetailedInvoiceRow[]
): number =>
  requirements.reduce((sum, requirement) => {
    const overhead = exploitableOverheadKm(requirement.id, invoiceRows)
    return sum + (overhead ?? 0)
  }, 0)

export const recommendedCars = (
  overhead: number | undefined,
  totalOverhead: number,
  availableVehicles: number
): number | undefined => {
  if (overhead === undefined || totalOverhead === 0) {
    return undefined
  }
  return (overhead / totalOverhead) * availableVehicles
}

export const availableVehiclesInWindow = (
  driveSubintervals: DriveSubinterval[],
  intervalStart: string,
  intervalEnd: string
): number =>
  uniq(
    driveSubintervals
      .filter(({ interval }) =>
        intervalContainedInWindow(
          { start: interval.starting_from, end: interval.ending_at },
          intervalStart,
          intervalEnd
        )
      )
      .map(interval => interval.vehicle_id)
  ).length

export const fulfillmentForRequirement = (
  fulfillment: RequirementFulfillment[],
  requirementId: number
): RequirementFulfillment | undefined => fulfillment.find(item => item.requirements.id === requirementId)

export const drivesInExactInterval = (
  driveSubintervals: DriveSubinterval[],
  intervalStart: string,
  intervalEnd: string
): DriveSubinterval['drives'] =>
  driveSubintervals
    .filter(({ interval }) =>
      intervalsMatch(
        { start: interval.starting_from, end: interval.ending_at },
        { start: intervalStart, end: intervalEnd }
      )
    )
    .flatMap(subinterval => subinterval.drives)
